/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, inject, Injector } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { concat, Observable } from 'rxjs';
import { first, toArray } from 'rxjs/operators';
import { uuid } from '@src/app/model/abstract-object';
import { Ballot } from '@src/app/model/ballot';
import { Forum } from '@src/app/model/forum';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { RequestService } from '@src/app/modules/crm/request.service';
import { BallotService } from '@src/app/modules/social/ballot-page/ballot.service';
import { BALLOT_TEAM_ID, BallotEditorComponent } from '@src/app/modules/social/ballot-editor/ballot-editor.component';
import { ForumService } from '@src/app/modules/social/forum/forum.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { BallotPageNavRoute } from '@src/app/routes/app-router';

/**
 * Puts a request on the agenda of a channel's meetings: escalate it to a channel so it is on every
 * future meeting there, tick meetings already scheduled, or schedule a special purpose meeting for it.
 * The channel and ticked meetings are saved together on Finish.
 * Channels and meetings belong to the OMC, so everything is fetched in the request's own team.
 */
@Component({
    selector: 'app-request-agenda-dialog',
    templateUrl: './request-agenda-dialog.component.html',
    styleUrls: ['./request-agenda-dialog.component.scss'],
    imports: [DatePipe, FormsModule, MatButtonModule, MatCheckboxModule, MatSelectModule, MatTooltipModule,
        RouterLink, DialogBannerComponent]
})
export class RequestAgendaDialogComponent {
    dialogRef = inject<MatDialogRef<RequestAgendaDialogComponent>>(MatDialogRef);
    private sr = inject<ServiceRequest>(MAT_DIALOG_DATA);
    private requestSvc = inject(RequestService);
    private ballotSvc = inject(BallotService);
    private currentUserSvc = inject(CurrentUserService);
    private injector = inject(Injector);

    channels: Forum[] = [];
    openBallots: Ballot[] = [];
    selectedForumId: uuid = this.sr.forumId;
    scheduleNewMeeting = false;
    private linkedBallotIds = new Set<uuid>((this.sr.ballots ?? []).map(link => link.ballotId));
    private ticked = new Set<uuid>();
    private channelsLoaded = false;
    private ballotsLoaded = false;

    get ready() {
        return this.channelsLoaded && this.ballotsLoaded;
    }

    get channelChanged() {
        return !!this.selectedForumId && this.selectedForumId !== this.sr.forumId;
    }

    get hasChanges() {
        return this.channelChanged || this.ticked.size > 0 || this.scheduleNewMeeting;
    }

    constructor() {
        const channelFilter = new HttpParams().set('privilege', 'canCreate');
        inject(ForumService).get<Forum>(false, channelFilter, this.sr.teamId).pipe(first()).subscribe(channels => {
            this.channels = channels;
            this.channelsLoaded = true;
        });
        const ballotFilter = new HttpParams().set('notClosed', 'true').set('privilege', 'canEdit');
        this.ballotSvc.get<Ballot>(false, ballotFilter, this.sr.teamId).pipe(first()).subscribe(ballots => {
            this.openBallots = ballots;
            this.ballotsLoaded = true;
        });
    }

    isOnAgenda(ballot: Ballot) {
        return this.linkedBallotIds.has(ballot.id);
    }

    isTicked(ballot: Ballot) {
        return this.isOnAgenda(ballot) || this.ticked.has(ballot.id);
    }

    setTicked(ballot: Ballot, checked: boolean) {
        if (checked) {
            this.ticked.add(ballot.id);
        } else {
            this.ticked.delete(ballot.id);
        }
    }

    ballotLink(ballot: Ballot) {
        return BallotPageNavRoute.getIdUrl(ballot.id, ballot.teamId);
    }

    addToAgendaHint(ballot: Ballot) {
        if (this.isOnAgenda(ballot)) {
            return $localize`This request is already on the agenda for this meeting`;
        }
        return this.selectedForumId
            ? $localize`Add this to the agenda for the ${ballot.title} meeting`
            : $localize`Escalate this to the ${ballot.forum?.name} channel and add it to the agenda for the ${ballot.title} meeting`;
    }

    finish() {
        concat(...this.pendingSaves()).pipe(toArray()).subscribe(results => {
            if (!results.every(saved => !!saved)) {
                return;
            }
            if (this.scheduleNewMeeting) {
                this.openMeetingScheduler();
            } else {
                this.dialogRef.close();
            }
        });
    }

    private pendingSaves(): Observable<unknown>[] {
        const saves: Observable<unknown>[] = [];
        if (this.channelChanged) {
            const update = { id: this.sr.id, revision: this.sr.revision, teamId: this.sr.teamId, forumId: this.selectedForumId };
            saves.push(this.requestSvc.put(update as ServiceRequest));
        }
        this.openBallots.filter(ballot => this.ticked.has(ballot.id))
            .forEach(ballot => saves.push(this.ballotSvc.linkRequest(ballot, this.sr.id, this.sr.teamId)));
        return saves;
    }

    /** The other choices are already saved by now, so this dialog closes whether or not a meeting is scheduled */
    private openMeetingScheduler() {
        const injector = Injector.create({
            providers: [BallotEditorComponent, { provide: BALLOT_TEAM_ID, useValue: this.forcedBallotTeamId() }],
            parent: this.injector,
        });
        const forumId = this.selectedForumId ?? this.sr.forumId;
        injector.get(BallotEditorComponent).scheduleMeetingFor(this.sr, forumId).afterClosed().pipe(first())
            .subscribe(() => {
                injector.destroy();
                this.dialogRef.close();
            });
    }

    /** The ballot editor treats a forced team as an agent standing outside the OMC - only force it then */
    private forcedBallotTeamId(): uuid {
        return this.sr.teamId === this.currentUserSvc.currentTeam?.id ? null : this.sr.teamId;
    }
}
